import {
	type IndoorRoomConfig,
	normalizeStoredIndoorRoom,
} from "../domain/indoor/indoorRoom";
/**
 * Share Config — encode / decode sauna configuration to/from URL-safe strings.
 *
 * Uses JSON → base64url encoding. No external dependencies.
 * Configs are typically ~200-400 bytes, well within URL length limits.
 */

import { z } from "zod";
import {
	type FinishConfig,
	isDefaultFinishes,
	normalizeFinishes,
} from "../domain/finishes/types";
import {
	DEFAULT_SITE_CONDITIONS,
	type SiteConditions,
} from "../domain/foundation/siteConditions";
import type { WallOpening } from "../domain/framing/generateFramedWall";
import {
	normalizeWallHeightM,
	resolveWallHeightM,
} from "../domain/geometry/buildingDimensions";
import { normalizeHeaterModelId } from "../domain/heater/heaterCatalog";
import { normalizeInteriorLayout } from "../domain/interior/normalizeInteriorLayout";
import type { InteriorLayout } from "../domain/interior/types";
import {
	type ClimateProfile,
	type LocationMode,
	type Market,
	normalizeClimateProfile,
} from "../domain/location/types";
import { normalizeRoofConfig } from "../domain/roof/roofConfig";
import {
	type SaunaRotationDeg,
	snapRotationDeg,
} from "../domain/viewer/saunaRotation";
import {
	type LegacyTimeOfDay,
	normalizeTimeOfDay,
	type TimeOfDayValue,
} from "../domain/viewer/timeOfDay";
import type {
	DeckConfig,
	HeaterType,
	RoofConfig,
	TwoRoomConfig,
} from "../engines/types";
import type { SaunaType } from "../types/design";

// ─── Shareable Config Type ──────────────────────────────────

export interface ShareableConfig {
	market?: Market;
	locationMode?: LocationMode;
	climateProfile?: ClimateProfile;
	/** Sauna type */
	type: SaunaType;
	/** Width in metres */
	width: number;
	/** Length in metres */
	length: number;
	/** Two-room divider split ratio */
	partitionSplit?: number;
	/** Two-room entry wall */
	entryWall?: TwoRoomConfig["entryWall"];
	/** Two-room glazing style */
	glazingStyle?: TwoRoomConfig["glazingStyle"];
	/** Two-room sauna position */
	saunaPosition?: TwoRoomConfig["saunaPosition"];
	/** Whether the two-room layout keeps a separate relax room */
	hasRelaxRoom?: boolean;
	/** Viewer sun/time slider, 0 midday → 0.7 golden hour → 1 night */
	timeOfDay?: TimeOfDayValue;
	/** Whole-building orientation on the plot, one of 0/90/180/270 degrees */
	rotationDeg?: SaunaRotationDeg;
	/** The wizard's "2/4/6 personer" size-tier choice, if any still applies. */
	capacityHint?: number | null;
	/** Roof geometry */
	roofConfig: RoofConfig;
	/** Heater type */
	heaterType: HeaterType;
	/** Catalog heater model (issue 159) */
	heaterModelId?: string | null;
	/** Glass area in m² */
	glassAreaSqm: number;
	/** Stone area in m² */
	stoneAreaSqm: number;
	/** Is outdoor log cabin */
	isOutdoorLogCabin: boolean;
	/** Includes shower */
	includesShower: boolean;
	saunaLighting?: "wall" | "none";
	/** Preferred heater/chimney center along the room length, in mm */
	heaterPlacementZMm?: number;
	/** Ground and foundation settings */
	siteConditions: SiteConditions;
	/** Wall openings (doors/windows) */
	openings: WallOpening[];
	/** Optional deck / terrace config */
	deckConfig?: DeckConfig | null;
	/**
	 * User-authored interior layout. Omitted when the design has none, which is
	 * both the common case and what keeps existing share URLs the length they
	 * are — but when the user HAS moved a bench, a link without it shares a
	 * different sauna than the one on their screen.
	 */
	interiorLayout?: InteriorLayout | null;
	/**
	 * Karaktärsval. Omitted from the wire when they are the defaults, so every
	 * link written before issue 134 — and every link for a design nobody
	 * restyled — stays exactly as short as it is today.
	 */
	finishes?: FinishConfig | null;
	/**
	 * Chosen wall height, m (issue 155). Omitted from the wire when unset, so
	 * every older link opens at the default height it was drawn with.
	 */
	wallHeightM?: number | null;
	/**
	 * Inomhus only (issues 174/175). Omitted from the wire when it is the
	 * default, so a free-standing indoor sauna's link stays as short as before.
	 */
	indoorRoom?: IndoorRoomConfig | null;
	/** Postal code (optional) */
	postalCode?: string;
	/** Project name (optional) */
	projectName?: string;
}

// ─── Compact wire format (shorter keys = shorter URLs) ──────

interface WireFormat {
	m?: Market; // market
	lm?: LocationMode; // locationMode
	cp?: ClimateProfile; // climateProfile
	t: string; // type
	w: number; // width
	l: number; // length
	ps?: number; // partitionSplit
	ew?: TwoRoomConfig["entryWall"]; // entryWall
	gs?: TwoRoomConfig["glazingStyle"]; // glazingStyle
	sp?: TwoRoomConfig["saunaPosition"]; // saunaPosition
	hr?: boolean; // hasRelaxRoom
	td?: TimeOfDayValue | LegacyTimeOfDay; // timeOfDay
	rot?: number; // rotationDeg
	ch?: number; // capacityHint
	r?: RoofConfig; // roofConfig
	h: string; // heaterType
	hm?: string; // heaterModelId
	g: number; // glassAreaSqm
	s: number; // stoneAreaSqm
	c: boolean; // isOutdoorLogCabin
	sh: boolean; // includesShower
	sl?: "wall" | "none"; // saunaLighting
	hp?: number; // heaterPlacementZMm
	sc?: SiteConditions; // siteConditions
	o: WallOpening[]; // openings
	d?: DeckConfig | null; // deckConfig
	il?: InteriorLayout; // interiorLayout
	fn?: FinishConfig; // finishes
	wh?: number; // wallHeightM
	ir?: IndoorRoomConfig; // indoorRoom
	z?: string; // postalCode
	n?: string; // projectName
}

// ─── Wire format validation ─────────────────────────────────
//
// Validates URL-supplied input strictly before fromWire() touches it.
// Enums (sauna type, heater type, market, location mode) are checked
// against the allowed values — fromWire previously cast `unknown` to
// these union types and trusted the cast, which let malformed share
// URLs propagate bogus values into the engines.

const WireFormatSchema: z.ZodType<WireFormat> = z.object({
	m: z.enum(["se", "intl"]).optional(),
	lm: z.enum(["postal_code", "manual_climate"]).optional(),
	cp: z.unknown().optional() as z.ZodType<ClimateProfile | undefined>,
	t: z.enum(["fyrkantig", "tunna", "tvarum", "inomhus"]),
	w: z.number(),
	l: z.number(),
	ps: z.number().optional(),
	ew: z.enum(["short", "long"]).optional(),
	gs: z.enum(["standard", "panorama"]).optional(),
	sp: z.enum(["left", "right"]).optional(),
	hr: z.boolean().optional(),
	td: z.union([z.number(), z.enum(["day", "evening"])]).optional(),
	rot: z.number().optional(),
	ch: z.number().optional(),
	r: z.unknown().optional() as z.ZodType<RoofConfig | undefined>,
	h: z.enum(["wood", "electric"]),
	hm: z.string().max(64).optional(),
	g: z.number(),
	s: z.number(),
	c: z.boolean(),
	sh: z.boolean(),
	sl: z.enum(["wall", "none"]).optional(),
	hp: z.number().optional(),
	sc: z.unknown().optional() as z.ZodType<SiteConditions | undefined>,
	o: z.array(z.unknown()).optional() as z.ZodType<WallOpening[] | undefined>,
	d: z.unknown().optional() as z.ZodType<DeckConfig | null | undefined>,
	// Structure-checked by normalizeInteriorLayout in fromWire, which degrades a
	// malformed layout to "none" rather than failing the whole URL.
	il: z.unknown().optional() as z.ZodType<InteriorLayout | undefined>,
	// Same contract as `il`: URL-supplied, so validated loosely here and
	// normalized per field in fromWire — a hand-edited link must degrade to the
	// defaults, not fail to open.
	fn: z.unknown().optional() as z.ZodType<FinishConfig | undefined>,
	// Snapped and clamped in fromWire, like the saved column.
	wh: z.number().optional(),
	// Same contract as `fn`: normalized in fromWire.
	ir: z.unknown().optional() as z.ZodType<IndoorRoomConfig | undefined>,
	z: z.string().optional(),
	n: z.string().optional(),
}) as z.ZodType<WireFormat>;

// ─── Encode ─────────────────────────────────────────────────

function toWire(config: ShareableConfig): WireFormat {
	return {
		...(config.market ? { m: config.market } : {}),
		...(config.locationMode ? { lm: config.locationMode } : {}),
		...(config.climateProfile ? { cp: config.climateProfile } : {}),
		t: config.type,
		w: config.width,
		l: config.length,
		...(config.partitionSplit !== undefined
			? { ps: config.partitionSplit }
			: {}),
		...(config.entryWall !== undefined ? { ew: config.entryWall } : {}),
		...(config.glazingStyle !== undefined ? { gs: config.glazingStyle } : {}),
		...(config.saunaPosition !== undefined ? { sp: config.saunaPosition } : {}),
		...(config.hasRelaxRoom !== undefined ? { hr: config.hasRelaxRoom } : {}),
		...(config.timeOfDay !== undefined
			? { td: normalizeTimeOfDay(config.timeOfDay) }
			: {}),
		...(config.rotationDeg ? { rot: snapRotationDeg(config.rotationDeg) } : {}),
		...(config.capacityHint ? { ch: config.capacityHint } : {}),
		r: config.roofConfig,
		h: config.heaterType,
		...(config.heaterModelId ? { hm: config.heaterModelId } : {}),
		g: config.glassAreaSqm,
		s: config.stoneAreaSqm,
		c: config.isOutdoorLogCabin,
		sh: config.includesShower,
		...(config.saunaLighting === "none" ? { sl: "none" as const } : {}),
		...(config.heaterPlacementZMm !== undefined
			? { hp: config.heaterPlacementZMm }
			: {}),
		sc: config.siteConditions,
		o: config.openings,
		...(config.deckConfig !== undefined ? { d: config.deckConfig } : {}),
		...(config.interiorLayout ? { il: config.interiorLayout } : {}),
		...(config.finishes && !isDefaultFinishes(config.finishes)
			? { fn: config.finishes }
			: {}),
		...(config.wallHeightM != null && config.type !== "tunna"
			? { wh: resolveWallHeightM(config.wallHeightM) }
			: {}),
		...(config.type === "inomhus" &&
		normalizeStoredIndoorRoom(config.indoorRoom)
			? { ir: normalizeStoredIndoorRoom(config.indoorRoom) as IndoorRoomConfig }
			: {}),
		...(config.postalCode ? { z: config.postalCode } : {}),
		...(config.projectName ? { n: config.projectName } : {}),
	};
}

function fromWire(wire: WireFormat): ShareableConfig {
	const market = wire.m ?? (wire.cp ? "intl" : "se");
	const locationMode = wire.lm ?? (wire.cp ? "manual_climate" : "postal_code");

	return {
		market,
		locationMode,
		climateProfile: wire.cp ? normalizeClimateProfile(wire.cp) : undefined,
		type: wire.t as SaunaType,
		width: wire.w,
		length: wire.l,
		partitionSplit: wire.ps,
		entryWall: wire.ew,
		glazingStyle: wire.gs,
		saunaPosition: wire.sp,
		hasRelaxRoom: wire.hr,
		timeOfDay: normalizeTimeOfDay(wire.td),
		rotationDeg: snapRotationDeg(wire.rot),
		capacityHint: wire.ch ?? null,
		roofConfig: normalizeRoofConfig(wire.t as SaunaType, wire.r),
		heaterType: wire.h as HeaterType,
		heaterModelId: normalizeHeaterModelId(wire.hm),
		glassAreaSqm: wire.g,
		stoneAreaSqm: wire.s,
		isOutdoorLogCabin: wire.c,
		includesShower: wire.sh,
		saunaLighting: wire.sl,
		heaterPlacementZMm: wire.hp,
		siteConditions: { ...DEFAULT_SITE_CONDITIONS, ...(wire.sc ?? {}) },
		openings: wire.o ?? [],
		deckConfig: wire.d ?? null,
		interiorLayout: normalizeInteriorLayout(wire.il),
		finishes: normalizeFinishes(wire.fn),
		wallHeightM: normalizeWallHeightM(wire.wh),
		indoorRoom:
			wire.t === "inomhus" ? normalizeStoredIndoorRoom(wire.ir) : null,
		postalCode: wire.z,
		projectName: wire.n,
	};
}

/**
 * Encode a shareable config into a URL-safe string.
 *
 * Uses JSON → base64url (no padding).
 */
export function encodeShareConfig(config: ShareableConfig): string {
	const json = JSON.stringify(toWire(config));
	// btoa works on latin1, so we encode to UTF-8 first for safety
	const base64 = btoa(
		encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, p1) =>
			String.fromCharCode(parseInt(p1, 16)),
		),
	);
	// Make URL-safe: + → -, / → _, remove padding =
	return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Decode a URL-safe string back into a ShareableConfig.
 *
 * Returns null if the input is malformed or cannot be parsed.
 */
export function decodeShareConfig(encoded: string): ShareableConfig | null {
	try {
		// Restore standard base64
		let base64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
		// Re-add padding
		while (base64.length % 4 !== 0) {
			base64 += "=";
		}
		const json = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
				.join(""),
		);
		const parsed = WireFormatSchema.safeParse(JSON.parse(json));
		if (!parsed.success) {
			return null;
		}

		return fromWire(parsed.data);
	} catch {
		return null;
	}
}

/**
 * Build a full share URL from the current origin and a config.
 */
export function buildShareUrl(config: ShareableConfig): string {
	const encoded = encodeShareConfig(config);
	const origin = typeof window !== "undefined" ? window.location.origin : "";
	return `${origin}/dela/${encoded}`;
}
